import {
  ExceptionReasonSchema,
  type ExceptionKind,
  type ExceptionReason,
  type ScanCheckFindingDTO,
} from '@deepguide-ai/types'

export type ResourceExceptionKind = Exclude<
  ExceptionKind,
  'SCOPE_INCLUDE' | 'TEST'
>

export const REASON_LABELS: Record<ExceptionReason, string> = {
  AUTO_MANAGED: 'Auto-managed by cloud provider',
  STATIC_WEBSITE: 'Static website / public asset',
  VENDOR_MANAGED: 'Vendor-managed (Databricks, Synapse, etc.)',
  DEV_SANDBOX: 'Dev / sandbox environment',
  ACCEPTED_RISK: 'Accepted risk',
  COMPENSATING_CONTROL: 'Compensating control',
  OTHER: 'Other',
  OUT_OF_SCOPE_DEV: 'Dev / staging (non-production environment)',
  OUT_OF_SCOPE_VENDOR: 'Vendor-managed infrastructure',
  OUT_OF_SCOPE_INTERNAL: 'Internal tooling (no customer data)',
  OUT_OF_SCOPE_DECOMMISSION: 'Pending decommission',
}

/**
 * Reasons offered per exception kind (RFC-182): FINDING rows keep the
 * original per-check taxonomy; SCOPE rows use the OUT_OF_SCOPE_* family.
 * OTHER stays available in both. Derived from the generated enum so a
 * future reason value can't silently miss both pickers.
 */
const isScopeReason = (r: ExceptionReason) => r.startsWith('OUT_OF_SCOPE_')

export const FINDING_REASONS: ExceptionReason[] =
  ExceptionReasonSchema.options.filter(
    (r) => !isScopeReason(r) && r !== 'COMPENSATING_CONTROL'
  )

export const SCOPE_REASONS: ExceptionReason[] = [
  ...ExceptionReasonSchema.options.filter(isScopeReason),
  'OTHER',
]

/**
 * Reason to select when the exception kind changes: keep the seeded
 * reason if it belongs to the new kind's family, else the family default.
 */
export function defaultReasonForKind(
  kind: ResourceExceptionKind,
  seed?: ExceptionReason,
): ExceptionReason {
  const family = kind === 'SCOPE' ? SCOPE_REASONS : FINDING_REASONS
  return seed && family.includes(seed) ? seed : family[0]
}

/** Shared copy for the exception popovers + SCOPE affordances (both dialogs). */
export const KIND_POPOVER_HINTS: Record<ResourceExceptionKind, string> = {
  FINDING: 'Stays linked to this resource + check; auditors see the trail.',
  SCOPE: 'Suppresses every check for this resource; auditors see the trail.',
}
export const SCOPE_SUMMARY_LABEL = 'Out of scope (all checks)'
export const SCOPE_REMOVE_LABEL = 'Return to scope'
export const SCOPE_REMOVE_TITLE =
  'Return to scope. Findings resume on every check for this resource'
export const SCOPE_REMOVED_TOAST = 'Resource returned to scope'

export interface AutoManagedHint {
  reason: ExceptionReason
  /** Short label used as the auto-filled justification. */
  label: string
  /** Inline-banner copy explaining why this finding looks auto-managed. */
  suggestion: string
}

export function isFlaggedStatus(
  status: ScanCheckFindingDTO['status'],
): boolean {
  return status === 'fail' || status === 'warn'
}

const FLOWLOGS_RG = '/networkwatcherrg/'
const FLOWLOGS_ACCOUNT = '/storageaccounts/flowlogs'
const STATIC_WEB_CONTAINER = '/$web'
const CLOUD_SHELL_RG = '/cloud-shell-storage-'

/**
 * Detect well-known auto-managed Azure resource patterns so the user
 * can one-click scope them out. Conservative — only flags patterns we're
 * confident about. Returns null when no pattern matches.
 */
export function detectAutoManagedPattern(
  finding: ScanCheckFindingDTO,
): AutoManagedHint | null {
  const id = finding.resourceId?.toLowerCase()
  if (!id) return null

  if (id.includes(FLOWLOGS_RG) && id.includes(FLOWLOGS_ACCOUNT)) {
    return {
      reason: 'AUTO_MANAGED',
      label: 'Network Watcher flow log destination, auto-managed by Azure.',
      suggestion: 'Looks like a Network Watcher flow log destination.',
    }
  }

  if (id.includes(STATIC_WEB_CONTAINER)) {
    return {
      reason: 'STATIC_WEBSITE',
      label: '$web container. Static site hosting requires public access.',
      suggestion: 'Looks like a static-site $web container.',
    }
  }

  if (id.includes(CLOUD_SHELL_RG)) {
    return {
      reason: 'AUTO_MANAGED',
      label: 'Cloud Shell storage account, auto-managed by Azure.',
      suggestion: 'Looks like a Cloud Shell storage account.',
    }
  }

  return null
}
